import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';

function Projects() {
  const [projects, setProjects] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(4);
  const [filterTag, setFilterTag] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    const updateItemsPerPage = () => {
      if (window.innerWidth >= 1024) {
        setItemsPerPage(6);
      } else {
        setItemsPerPage(4);
      }
    };

    updateItemsPerPage();
    window.addEventListener('resize', updateItemsPerPage);
    return () => window.removeEventListener('resize', updateItemsPerPage);
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'projects'),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const projectsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProjects(projectsList);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = filterTag === 'All' 
    ? projects 
    : projects.filter(project => project.tag === filterTag);

  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProjects = filteredProjects.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFilterChange = (tag) => {
    setFilterTag(tag);
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  const tags = ['All', ...new Set(projects.map(p => p.tag))];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFB7CE]">
        <div className="text-white text-2xl font-bold">Loading projects...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFB7CE] py-12 px-4 sm:px-6 lg:px-8" id="projects">
      <div className="max-w-7xl mx-auto">
        <h2 className='text-white font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-center mb-8'>
          PROJECTS
        </h2>

        {/* Filter Tags */}
        <div className='flex flex-wrap justify-center gap-3 mb-12'>
          {tags.map(tag => (
            <button
              key={tag}
              onClick={() => handleFilterChange(tag)}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-200 ${
                filterTag === tag
                  ? 'bg-white text-[#FFB7CE] shadow-lg scale-105'
                  : 'bg-[#89CFF0] text-white hover:bg-[#6BB8E0]'
              }`}
            >
              {tag}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        {currentProjects.length === 0 ? (
          <div className='text-center text-white text-xl py-12'>
            No projects found in this category.
          </div>
        ) : (
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-12'>
            {currentProjects.map((item) => (
              <div 
                key={item.id} 
                className='bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105'
              >
                <div className='relative'>
                  <img
                    src={item.img}
                    alt={item.alternative}
                    className='rounded-t-xl w-full h-48 object-cover'
                  />
                  <div className='bg-[#89CFF0] w-[70px] rounded-2xl flex items-center justify-center h-[30px] absolute top-2 text-white right-2 text-xs font-semibold'>
                    {item.tag}
                  </div>
                  {item.clientName && item.clientName !== 'Personal' && (
                    <div className='bg-[#FFB7CE] px-3 py-1 rounded-2xl absolute top-2 left-2 text-white text-xs font-semibold'>
                      {item.status}
                    </div>
                  )}
                </div>
                <div className='p-4'>
                  <h4 className='text-center text-lg font-semibold mb-2 text-gray-800'>
                    {item.title}
                  </h4>
                  <p className='text-gray-600 text-sm leading-relaxed mb-4'>
                    {item.description}
                    {item.more && (
                      <a 
                        href={item.more}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-[#89CFF0] hover:underline ml-1'
                      >
                        Read More
                      </a>
                    )}
                  </p>
                  <div className='flex justify-center'>
                    <a 
                      href={item.link} 
                      target="_blank" 
                      rel='noopener noreferrer'
                      className='px-6 py-2 bg-[#89CFF0] text-white rounded-xl hover:bg-[#6BB8E0] transition-colors duration-200 text-sm font-medium'
                    >
                      View Project
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className='flex items-center justify-center gap-2 mb-8'>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                currentPage === 1
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-[#89CFF0] text-white hover:bg-[#6BB8E0]'
              }`}
            >
              Prev
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
              <button
                key={pageNumber}
                onClick={() => handlePageChange(pageNumber)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  currentPage === pageNumber
                    ? 'bg-white text-[#FFB7CE] shadow-lg'
                    : 'bg-[#89CFF0] text-white hover:bg-[#6BB8E0]'
                }`}
              >
                {pageNumber}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                currentPage === totalPages
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-[#89CFF0] text-white hover:bg-[#6BB8E0]'
              }`}
            >
              Next
            </button>
          </div>
        )}

        {/* Page Info */}
        {filteredProjects.length > 0 && (
          <div className='text-white text-sm text-center'>
            Showing {startIndex + 1}-{Math.min(endIndex, filteredProjects.length)} of {filteredProjects.length} projects
          </div>
        )}
      </div>
    </div>
  );
}

export default Projects;